// Match celebration. Falling plum petals replace party confetti; the photo
// is framed in a moon window (圆窗) with a 缘 seal tucked at its bottom-right.
// Petals are generated once at mount so the keyframe animation can read
// per-piece offsets via CSS custom properties without per-frame React work.

import { useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import type { Persona } from '@/lib/types';
import { Portrait } from '@/components/portraits';
import { SealStamp } from '@/components/visual/SealStamp';
import { C } from '@/styles/tokens';

interface MatchModalProps {
  persona: Persona;
  onChat: () => void;
  onContinue: () => void;
}

interface Petal {
  cx: string;
  cy: string;
  cr: string;
  delay: string;
  color: string;
  left: string;
  top: string;
  rotInit: number;
}

function makePetals(count: number): Petal[] {
  // Plum-pink + coral + soft gold range — closer to actual falling 梅花.
  const palette = ['#E76A66', '#F6A6A0', '#FFB5A8', '#C9A36A'];
  return Array.from({ length: count }, (_, i) => ({
    // Drift sideways while falling. cx is horizontal drift, cy is downward.
    cx: `${(Math.random() - 0.5) * 200}px`,
    cy: `${260 + Math.random() * 240}px`,
    cr: `${(Math.random() - 0.5) * 540}deg`,
    delay: `${Math.random() * 0.9}s`,
    color: palette[i % palette.length]!,
    left: `${Math.random() * 100}%`,
    top: `-20px`,
    rotInit: Math.floor(Math.random() * 360),
  }));
}

export function MatchModal({ persona, onChat, onContinue }: MatchModalProps) {
  const petals = useMemo(() => makePetals(28), []);

  return (
    <div
      role="dialog"
      aria-modal
      aria-label={`Matched with ${persona.en}`}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(26,22,20,0.7)', backdropFilter: 'blur(8px)' }}
    >
      {/* Falling plum petals — 8×10 with one asymmetric rounded corner so they
          read as leaf-like rather than confetti squares. */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {petals.map((p, i) => (
          <span
            key={i}
            aria-hidden
            className="absolute block anim-petal-fall"
            style={
              {
                left: p.left,
                top: p.top,
                width: 10,
                height: 8,
                background: p.color,
                borderRadius: '60% 30% 60% 30%',
                transform: `rotate(${p.rotInit}deg)`,
                animationDelay: p.delay,
                opacity: 0.92,
                ['--cx' as string]: p.cx,
                ['--cy' as string]: p.cy,
                ['--cr' as string]: p.cr,
              } as React.CSSProperties
            }
          />
        ))}
      </div>

      <div
        className="relative w-full max-w-sm rounded-[28px] anim-scale-in overflow-hidden shadow-paper-lg"
        style={{ background: C.cream, border: `1px solid ${C.mist}` }}
      >
        {/* Moon window (圆窗) — portrait clipped to a circle with a thin
            vermillion ring. Sits on a cream stage that lets the round shape
            read as a window cut into the card. */}
        <div className="relative pt-8 pb-6 flex justify-center">
          <div
            className="relative"
            style={{ width: 192, height: 192 }}
          >
            <div
              className="absolute inset-0 rounded-full overflow-hidden"
              style={{
                border: `2px solid ${C.vermillion}`,
                boxShadow: `0 0 0 6px ${C.cream}, 0 12px 28px -8px rgba(120,90,70,0.25)`,
              }}
            >
              <Portrait persona={persona} />
            </div>
            {/* 缘 (destined connection) chop tucked at the moon window's
                lower-right, sitting half-on / half-off the ring. */}
            <div className="absolute" style={{ right: -10, bottom: -6 }} aria-hidden>
              <SealStamp char="缘" size={44} rotate={-10} />
            </div>
          </div>
        </div>

        <div className="relative px-7 pb-7 text-center">
          <div className="inline-flex items-center gap-2 mb-1 ff-display text-3xl italic" style={{ color: C.vermillionDeep }}>
            <Sparkles size={22} />
            It&apos;s a match
            <Sparkles size={22} />
          </div>
          <div className="ff-zh-serif text-base mb-3 opacity-70" style={{ color: C.vermillionDeep }}>
            配对成功
          </div>
          <div className="ff-zh-serif text-3xl font-bold mb-1" style={{ color: C.ink }}>
            {persona.name}
          </div>
          <div className="ff-body text-sm mb-4" style={{ color: C.inkLight }}>
            {persona.intro}
          </div>

          <button
            onClick={onChat}
            className="w-full py-3 rounded-full ff-body font-semibold text-white anim-pulse-glow inline-flex items-center justify-center gap-2"
            style={{ background: C.vermillion }}
          >
            Start chatting
            <span className="ff-zh-serif opacity-80">开始聊天</span>
          </button>
          <button
            onClick={onContinue}
            className="w-full mt-2 py-3 rounded-full ff-body font-medium inline-flex items-center justify-center gap-2"
            style={{ color: C.inkLight }}
          >
            Keep swiping
            <span className="ff-zh-serif opacity-70">继续</span>
          </button>
        </div>
      </div>
    </div>
  );
}
